import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

import type { CalendarAvailabilityData } from '../api/get-calendar-availability.server';

dayjs.extend(utc);
dayjs.extend(timezone);

/** Session id used for a synthesised payload, so nothing mistakes it for a real session. */
export const REQUEST_MODE_SESSION_ID = 0;

/** Working day offered when there is no calendar to check against. */
export const REQUEST_START_HOUR = 9;
export const REQUEST_END_HOUR = 17;

/** Gap between offered start times, in minutes. */
export const REQUEST_SLOT_INTERVAL_MINUTES = 30;

/** Meeting lengths offered, in minutes. The first is the default. */
export const REQUEST_DURATIONS = [30, 60];

/** Days generated ahead of today, matching the connected card's two-week window. */
export const REQUEST_WINDOW_DAYS = 14;

type Professional = NonNullable<CalendarAvailabilityData['professional']>;
type TeamMembers = CalendarAvailabilityData['teamMembers'];

export interface BuildRequestAvailabilityInput {
    professional: Professional;
    /** Public, approved team members. Every one of them is offered on every slot. */
    teamMembers: TeamMembers;
    /** The professional's own timezone, so the hours read as their working day. */
    timezone: string;
    /** Epoch ms treated as "now". Injectable so the generator is testable. */
    now?: number;
}

/**
 * Start times for one working day, as epoch ms.
 *
 * A meeting has to finish inside the working day, so the last start is
 * `REQUEST_END_HOUR` minus the meeting length. Times already past are dropped,
 * which empties out the current day over the course of an afternoon.
 */
const slotTimestampsForDay = (
    dateKey: string,
    zone: string,
    durationMinutes: number,
    nowMs: number,
): number[] => {
    const timestamps: number[] = [];
    const lastStartMinutes = REQUEST_END_HOUR * 60 - durationMinutes;

    for (
        let minutes = REQUEST_START_HOUR * 60;
        minutes <= lastStartMinutes;
        minutes += REQUEST_SLOT_INTERVAL_MINUTES
    ) {
        const hour = String(Math.floor(minutes / 60)).padStart(2, '0');
        const minute = String(minutes % 60).padStart(2, '0');
        const start = dayjs.tz(`${dateKey}T${hour}:${minute}`, zone);
        const timestamp = start.valueOf();
        if (timestamp > nowMs) {
            timestamps.push(timestamp);
        }
    }

    return timestamps;
};

/**
 * Weekday date keys (YYYY-MM-DD) from today, in the professional's timezone.
 */
const workingDayKeys = (zone: string, nowMs: number): string[] => {
    const keys: string[] = [];
    const start = dayjs(nowMs).tz(zone).startOf('day');

    for (let offset = 0; offset < REQUEST_WINDOW_DAYS; offset++) {
        const day = start.add(offset, 'day');
        const weekday = day.day();
        // 0 = Sunday, 6 = Saturday.
        if (weekday !== 0 && weekday !== 6) {
            keys.push(day.format('YYYY-MM-DD'));
        }
    }

    return keys;
};

/**
 * Builds a {@link CalendarAvailabilityData} payload for a professional with no
 * connected calendar, so the booking card can render its normal date and time
 * pickers against a plain working week instead of real availability.
 *
 * The hours are the professional's own (Monday to Friday, 9am to 5pm in their
 * timezone); the booking hook re-projects them into the visitor's timezone for
 * display, exactly as it does for a real session. Every team member is marked
 * available on every slot, because there is nothing to check them against.
 *
 * Submitting one of these is a request, not a booking. See BC-5174.
 */
export const buildRequestAvailability = ({
    professional,
    teamMembers,
    timezone: zone,
    now = Date.now(),
}: BuildRequestAvailabilityInput): CalendarAvailabilityData => {
    // Deliberately empty. A slot's available members exist so a booking can pick
    // whose calendar to write to, and nothing here writes to one — the identity
    // travels with the request as a TeamMember row instead. Populating it from
    // MemberID also put a 0 on every display-only member, which the request
    // route rejected outright.
    const availableMembers: { memberId: number }[] = [];

    const dayKeys = workingDayKeys(zone, now);

    const freeDaysByDuration = REQUEST_DURATIONS.map((duration) => ({
        duration,
        freeDays: [
            {
                timezone: zone,
                days: dayKeys
                    .map((date) => ({
                        date,
                        freeSlots: slotTimestampsForDay(
                            date,
                            zone,
                            duration,
                            now,
                        ).map((timestamp) => ({
                            label: dayjs(timestamp).tz(zone).format('h:mm A'),
                            timestamp,
                            availableMembers,
                        })),
                    }))
                    // A day whose slots have all passed offers nothing.
                    .filter((day) => day.freeSlots.length > 0),
            },
        ],
    }));

    // One team member reads as a one-on-one; more than one turns the heading
    // into the person picker the round-robin flow already renders. None at all
    // is the company itself: no face to offer, so no picker and no consultant.
    const isOneOnOne = teamMembers.length === 1;
    const isCompany = teamMembers.length === 0;

    return {
        sessionID: REQUEST_MODE_SESSION_ID,
        // The card names the chosen person itself, and follows the picker when
        // that changes, so this only has to be truthful for other consumers.
        sessionTitle: isOneOnOne
            ? `Meet with ${teamMembers[0]?.Name ?? 'the team'}`
            : `Meet with ${professional.Title ?? 'the team'}`,
        urlSegment: professional.URLSegment ?? '',
        isActive: true,
        locationType: 'online',
        locationString: null,
        meetingType: isOneOnOne || isCompany ? 'one-on-one' : 'random',
        meetingCover: null,
        meetingImage: null,
        customMessage: null,
        // `Checked` is what the booking hook reads as "offered", so every
        // duration a grid was built for has to carry it. Marking only the
        // first left the 60 minute grid built and unreachable.
        durations: REQUEST_DURATIONS.map((duration) => ({
            Duration: duration,
            Checked: true,
        })),
        emailReminders: [],
        professional,
        teamMembers,
        oneOnOneTeamMember: isOneOnOne ? teamMembers[0] : undefined,
        timezone: zone,
        freeDaysByDuration,
    };
};

/**
 * Recasts a request-mode payload as a request of the practice rather than of a
 * person, by emptying the roster the rest of the flow reads.
 *
 * The Our Team rail lists everyone the professional has made public, but a
 * roster row with no member account behind it (BM calls these "Display-only")
 * has no calendar to check and no inbox to deliver to, so
 * {@link getRequestAvailability} drops it. Left alone, asking to meet one of
 * those faces fell through to the picker's default and quietly offered a
 * different person — their name in the heading, their photo on the pane, and
 * their name in the enquiry that went out.
 *
 * Emptying `teamMembers` and every slot's `availableMembers` is what the
 * company tier already looks like for a professional with no public faces at
 * all, so this reuses that path rather than adding a third mode: no person
 * picker, no organiser on the enquiry, and copy that names the practice.
 *
 * See BC-5174.
 */
export const toCompanyTierAvailability = (
    availability: CalendarAvailabilityData,
): CalendarAvailabilityData => ({
    ...availability,
    meetingType: 'one-on-one',
    sessionTitle: `Meet with ${availability.professional?.Title ?? 'the team'}`,
    teamMembers: [],
    oneOnOneTeamMember: null,
    freeDaysByDuration: availability.freeDaysByDuration.map((entry) => ({
        ...entry,
        freeDays: entry.freeDays.map((timezoneDays) => ({
            ...timezoneDays,
            days: timezoneDays.days.map((day) => ({
                ...day,
                freeSlots: day.freeSlots.map((slot) => ({
                    ...slot,
                    availableMembers: [],
                })),
            })),
        })),
    })),
});
